import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/customer/auth';

export default function CustomerProfileSetup() {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [dob, setDob] = useState('');
  const [anniversary, setAnniversary] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!address.trim()) {
      toast.error('Please enter your address');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API_BASE}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: name.trim(),
          address: address.trim(),
          date_of_birth: dob || null,
          anniversary_date: anniversary || null,
        }),
      });
      if (!res.ok) throw new Error('Failed to save');

      const user = JSON.parse(localStorage.getItem('customer_user') || '{}');
      user.name = name.trim();
      user.address = address.trim();
      localStorage.setItem('customer_user', JSON.stringify(user));

      toast.success('Welcome aboard!');
      window.location.href = '/order';
    } catch {
      toast.error('Failed to save, please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="profile-setup-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <User size={28} weight="duotone" className="text-sspice" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 text-center mb-1">Tell us about you</h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          Just a couple of details before we start
        </p>

        <div className="space-y-3">
          <input
            data-testid="profile-setup-name-input"
            placeholder="Full name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
          />
          <textarea
            data-testid="profile-setup-address-input"
            placeholder="Address *"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all resize-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-gray-400 px-1">Birthday (optional)</label>
              <input
                data-testid="profile-setup-dob-input"
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className="w-full px-3 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-400 px-1">Anniversary (optional)</label>
              <input
                data-testid="profile-setup-anniversary-input"
                type="date"
                value={anniversary}
                onChange={(e) => setAnniversary(e.target.value)}
                className="w-full px-3 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
              />
            </div>
          </div>
        </div>

        <button
          data-testid="profile-setup-continue-btn"
          onClick={handleSubmit}
          disabled={loading || !name.trim() || !address.trim()}
          className="w-full mt-6 py-3.5 bg-sspice hover:bg-sspice-hover disabled:opacity-40 text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-all"
        >
          {loading ? 'Saving...' : 'Continue'}
          {!loading && <ArrowRight size={18} weight="bold" />}
        </button>
      </motion.div>
    </div>
  );
}
